// MultiCdmApp.js
// -----------------------------------------------------------------------------
// Root of the MultiCDM sub-app. Owns every piece of session state and hands the
// api down as a single prop, so no child fetches anything itself.
//
// It renders inside a fixed overlay in App.js, ABOVE the main app's view tree.
// That's what keeps App.js untouched: the running <Simulation> is mounted here,
// not in App's applicationView switch, so starting a case in a block can't
// disturb whatever the main app was showing.
//
// Screens:
//   sessions  MultiCdmSessionList   landing — In Progress | Completed
//   builder   MultiCdmCaseBuilder   pick 2-13 cases, name, settings
//   break     MultiCdmBreakScreen   between cases; also the pre-block screen
//   case      Simulation         the real thing, from the main app
//   review    MultiCdmReview        sidebar of cases -> grade view
//
// The case lifecycle:
//   break -> Continue -> startCase (+ incompleteStart when resuming)
//         -> Simulation runs its own questions, POSTs /grading.webapi and
//            fires gamification itself on the final one
//         -> displayGrading(gradingData) -> handleGradeCase records + hides it
//         -> back to break, grade hidden
//   ...until every case is done, then -> review.
//
// Note the asymmetry with Multi CCS: there, MultiCdmApp calls a gradeCase()
// api function itself and wraps triggerGamificationEvent in a no-op to avoid
// double-firing it. CDM's <Simulation> does both of those itself, so this
// component only reacts to what it's handed — see handleGradeCase and
// renderSimulation.
//
// Props:
//   route                string                                   (required)
//   userData             login payload                            (required)
//   userProfile          login profile                            (required)
//   primaryColor         string                                   (required)
//   selectedTheme        theme object                             (required)
//   themeNumber          raw theme index — same value App.js feeds NewGrading's
//                        own themeNumber prop; only used to dim MultiCdmGradeView's
//                        feedback cards on non-default themes         (optional)
//   onExit               () => void — close the overlay           (required)
//   setAuthenticationError () => void — App's 401 popup           (required)
//
//   The next five are exactly what App.js hands its own <Simulation>, so the
//   in-case Settings panel (skin, text size, font, split screen, confirm
//   before Next, lab/instruction placement) is 1:1 with a regular case:
//   simulationSkin       App's selectedSimulationSkin              (required)
//   simulationSkins      SIMULATION_SKINS                          (required)
//   changeSimulationSkin App.changeSimulationSkin                  (required)
//   savedReadingOptions  App's saved reading-options blob          (required)
//   saveReadingOptions   App.saveReadingOptions (merging, debounced)(required)
// -----------------------------------------------------------------------------

import React from "react";
import MDSpinner from "react-md-spinner";
import Simulation from "../Components/Simulation/Simulation";
import MultiCdmSessionList from "./MultiCdmSessionList";
import MultiCdmCaseBuilder from "./MultiCdmCaseBuilder";
import MultiCdmBreakScreen from "./MultiCdmBreakScreen";
import MultiCdmReview from "./MultiCdmReview";
import createMultiCdmApi, {
  CASE_STATUS,
  elapsedSeconds,
  isSessionComplete,
  nextCaseEntry,
  parseRememberedSettings,
} from "./multiCdmFetches";
import "./multiCdm.css";

class MultiCdmApp extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      screen: "sessions",

      // Case list — fetched once on mount, refreshed after each grade so
      // COMPLETED / AvgGradeForCase stay honest.
      cases: [],
      casesLoading: true,
      casesError: null,

      // Sessions
      sessions: [],
      sessionsLoading: true,
      sessionsError: null,

      // The open session (a normalized session object)
      session: null,

      // Flags
      creating: false,
      createError: null,
      starting: false,
      startError: null,
      savingSettings: false,
      renamingId: null,
      grading: false,
      // Toggled by <Simulation> around its own per-question network calls
      // (moveon.webapi, grading.webapi). Not currently rendered, but the real
      // setter avoids silently dropping state Simulation expects to control.
      fetchOutstanding: false,

      // Simulation payload for the case currently being run
      simulation: null,

      // Reading options, shared with the main grading page
      readingOptions: null,

      // The user's last-used block settings, from CUSTOMER.MultiCdmSettings on
      // the login payload. A new block starts from these; saving a block or
      // changing settings mid-block writes them back.
      rememberedSettings: parseRememberedSettings(
        props.userData ? props.userData.MultiCdmSettings : "",
      ),
    };

    this.api = createMultiCdmApi({
      route: props.route,
      userData: () => this.props.userData,
      userProfile: () => this.props.userProfile,
      onAuthError: () => {
        if (this.props.setAuthenticationError) {
          this.props.setAuthenticationError();
        }
      },
    });

    this._mounted = false;
    this._controller = null;
  }

  componentDidMount() {
    this.handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", this.handleBeforeUnload);

    this._mounted = true;
    this.fetchCases();
    this.fetchSessions();
    this.fetchReadingOptions();
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    this._mounted = false;
    if (this._controller) this._controller.abort();
  }

  // ── Loading ──────────────────────────────────────────────────────────────

  fetchCases = () => {
    this.setState({ casesLoading: true, casesError: null });
    this.api
      .listCases()
      .then((cases) => {
        if (!this._mounted) return;
        this.setState({ cases: cases, casesLoading: false });
      })
      .catch((error) => {
        if (!this._mounted) return;
        if (error && error.isAuthError) {
          this.setState({ casesLoading: false });
          return;
        }
        this.setState({
          casesError:
            "The case list wouldn't load. Check your connection and try again.",
          casesLoading: false,
        });
      });
  };

  fetchSessions = () => {
    this.setState({ sessionsLoading: true, sessionsError: null });
    this.api
      .listMultiCdmCases()
      .then((sessions) => {
        if (!this._mounted) return;
        this.setState({ sessions: sessions, sessionsLoading: false });
      })
      .catch((error) => {
        if (!this._mounted) return;
        // Same reasoning as fetchCases: don't layer a generic error message
        // on top of the 401 popup.
        if (error && error.isAuthError) {
          this.setState({ sessionsLoading: false });
          return;
        }
        this.setState({
          sessionsError:
            "Your blocks wouldn't load. Check your connection and try again.",
          sessionsLoading: false,
        });
      });
  };

  fetchReadingOptions = () => {
    this.api.getReadingOptions().then((options) => {
      if (!this._mounted || !options) return;
      this.setState({ readingOptions: options });
    });
  };

  // ── Session persistence ──────────────────────────────────────────────────

  // Single funnel for every CaseProgress write. Updates local state first so the
  // UI never waits on the network, then persists.
  //
  //   mutate(progress) -> next progress
  saveProgress(mutate, options) {
    const config = options || {};
    const session = this.state.session;
    if (!session) return Promise.resolve(null);

    const nextProgress = mutate(
      JSON.parse(JSON.stringify(session.CaseProgress)),
    );
    nextProgress.ModifiedDate = new Date().toISOString();

    const completed = isSessionComplete(nextProgress);
    const nextSession = Object.assign({}, session, {
      CaseProgress: nextProgress,
      Completed: completed,
    });

    this.setState({ session: nextSession });

    return this.api
      .saveMultiCdmCaseProgress({
        multiCdmCaseId: nextSession.ID,
        caseProgress: nextProgress,
        completed: completed,
      })
      .then((stored) => {
        if (!this._mounted) return null;
        // The backend fills in CurrentStateId for in-progress caseguids, so
        // prefer its copy when it hands one back.
        if (stored) {
          this.setState({ session: stored });
          return stored;
        }
        return nextSession;
      })
      .catch((error) => {
        // A failed progress write isn't fatal — the local copy is still right,
        // and the next write will carry the same blob. Surface it only when the
        // caller cares (starting a case does).
        if (config.rethrow) throw error;
        return nextSession;
      });
  }

  // ── Sessions ─────────────────────────────────────────────────────────────

  handleNewBlock = () => {
    this.setState({ screen: "builder", createError: null });
  };

  handleCreate = ({ title, caseIds, settings }) => {
    this.setState({ creating: true, createError: null });
    // Carry these settings into the next block the user builds.
    this.rememberSettings(settings);
    this.api
      .createMultiCdmCase({
        title: title,
        caseIds: caseIds,
        settings: settings,
      })
      .then((session) => {
        if (!this._mounted) return;
        this.setState({
          creating: false,
          session: session,
          screen: "break",
        });
        this.fetchSessions();
      })
      .catch((error) => {
        if (!this._mounted) return;
        if (error && error.isAuthError) {
          this.setState({ creating: false });
          return;
        }
        this.setState({
          creating: false,
          createError:
            "The block wouldn't start. Check your connection and try again.",
        });
      });
  };

  handleOpenSession = (session) => {
    // Always refetch: the stored CurrentStateId is filled in by the backend at
    // read time, and the list copy may predate the last case that was run.
    this.setState({ starting: true, startError: null });
    this.api
      .getMultiCdmCase(session.ID)
      .then((fresh) => {
        if (!this._mounted) return;
        const target = fresh || session;
        this.setState({
          starting: false,
          session: target,
          screen: target.Completed ? "review" : "break",
        });
      })
      .catch(() => {
        if (!this._mounted) return;
        // Fall back to the list's copy rather than blocking the user outright.
        this.setState({
          starting: false,
          session: session,
          screen: session.Completed ? "review" : "break",
        });
      });
  };

  handleRenameSession = (session, title) => {
    this.setState({ renamingId: session.ID });

    const applyLocally = (sessions) =>
      sessions.map(function (row) {
        if (row.ID !== session.ID) return row;
        return Object.assign({}, row, {
          Title: title,
          CaseProgress: Object.assign({}, row.CaseProgress, { Title: title }),
        });
      });

    this.api
      .updateMultiCdmCaseTitle(session.ID, title)
      .then(() => {
        if (!this._mounted) return;
        this.setState(function (prevState) {
          const next = {
            renamingId: null,
            sessions: applyLocally(prevState.sessions),
          };
          if (prevState.session && prevState.session.ID === session.ID) {
            next.session = Object.assign({}, prevState.session, {
              Title: title,
              CaseProgress: Object.assign({}, prevState.session.CaseProgress, {
                Title: title,
              }),
            });
          }
          return next;
        });
      })
      .catch(() => {
        if (!this._mounted) return;
        this.setState({ renamingId: null });
      });
  };

  backToSessions = () => {
    this.setState({
      screen: "sessions",
      session: null,
      simulation: null,
      startError: null,
    });
    this.fetchSessions();
  };

  // ── Settings ─────────────────────────────────────────────────────────────

  // Remember these settings for the next block. Updates local state right away
  // so the builder reflects them without waiting on the network, then persists.
  // Fire-and-forget: the block carries its own copy, so a failed write only
  // costs the user their defaults next time.
  rememberSettings = (settings) => {
    this.setState({ rememberedSettings: settings });
    this.api.saveMultiCdmSettings(settings);
  };

  handleSettings = (nextSettings) => {
    this.setState({ savingSettings: true });
    // Changing settings mid-block also updates what the next block starts from.
    this.rememberSettings(nextSettings);
    this.saveProgress(function (progress) {
      progress.Settings = nextSettings;
      return progress;
    }).then(() => {
      if (!this._mounted) return;
      this.setState({ savingSettings: false });
    });
  };

  // ── Break countdown ──────────────────────────────────────────────────────

  handleBreakTick = (remaining) => {
    const session = this.state.session;
    if (!session) return;

    const nextProgress = Object.assign({}, session.CaseProgress, {
      BreakRemainingSeconds: remaining,
    });
    this.setState({
      session: Object.assign({}, session, { CaseProgress: nextProgress }),
    });
  };

  // ── Running a case ───────────────────────────────────────────────────────

  caseRow(caseId) {
    const cases = this.state.cases;
    for (let i = 0; i < cases.length; i++) {
      if (cases[i].ID === caseId) return cases[i];
    }
    return null;
  }

  handleContinue = () => {
    const session = this.state.session;
    if (!session) return;

    const entry = nextCaseEntry(session.CaseProgress);
    if (!entry) {
      this.setState({ screen: "review" });
      return;
    }

    const caseRow = this.caseRow(entry.CaseId);
    if (!caseRow) {
      this.setState({
        startError:
          "Case " +
          entry.CaseId +
          " isn't in your case list. Reload and try again.",
      });
      return;
    }

    const resuming =
      entry.Status === CASE_STATUS.IN_PROGRESS &&
      entry.caseguid &&
      entry.CurrentStateId !== null &&
      entry.CurrentStateId !== undefined;

    this.setState({ starting: true, startError: null });

    if (resuming) {
      this.resumeCase(entry, caseRow);
    } else {
      this.startFreshCase(entry, caseRow);
    }
  };

  startFreshCase(entry, caseRow) {
    const timerSpeed = this.state.session.CaseProgress.Settings.TimerSpeed;
    this.api
      .startCase(caseRow, timerSpeed)
      .then((result) => {
        if (!this._mounted) return null;

        // Record the attempt before handing over to Simulation: if the user
        // walks away mid-case, this is what lets us find it again.
        const startedAt = new Date().toISOString();
        return this.saveProgress(function (progress) {
          progress.Cases = progress.Cases.map(function (row) {
            if (row.CaseId !== entry.CaseId) return row;
            return Object.assign({}, row, {
              Status: CASE_STATUS.IN_PROGRESS,
              caseguid: result.caseguid,
              StartedAt: startedAt,
            });
          });
          progress.BreakRemainingSeconds = null;
          return progress;
        }).then(() => {
          if (!this._mounted) return null;
          this.setState({
            starting: false,
            screen: "case",
            simulation: {
              caseData: caseRow,
              simulationData: result.simulationData,
              caseguid: result.caseguid,
              caseId: entry.CaseId,
              startedAt: startedAt,
            },
          });
          return null;
        });
      })
      .catch((error) => {
        if (!this._mounted) return;
        if (error && error.isAuthError) {
          this.setState({ starting: false });
          return;
        }
        this.setState({
          starting: false,
          startError:
            error && error.isCaseListError
              ? String(error.message).replace("Error: ", "")
              : "That case wouldn't start. Check your connection and try again.",
        });
      });
  }

  // Resuming needs BOTH endpoints: /startcase.webapi issues the caseguid that
  // /incompletestart.webapi wants, and the stored CurrentStateId picks out which
  // saved attempt to restore (the ialist can also hold attempts made outside
  // this block, so we match rather than take the newest).
  resumeCase(entry, caseRow) {
    const timerSpeed = this.state.session.CaseProgress.Settings.TimerSpeed;
    this.api
      .startCase(caseRow, timerSpeed)
      .then((result) => {
        if (!this._mounted) return null;

        const match = result.ialist.filter(function (attempt) {
          return attempt.CurrentStateId === entry.CurrentStateId;
        })[0];

        // The stored attempt is gone — start the case over rather than
        // resuming into someone else's state.
        if (!match) {
          const startedAt = new Date().toISOString();
          return this.saveProgress(function (progress) {
            progress.Cases = progress.Cases.map(function (row) {
              if (row.CaseId !== entry.CaseId) return row;
              return Object.assign({}, row, {
                Status: CASE_STATUS.IN_PROGRESS,
                caseguid: result.caseguid,
                CurrentStateId: null,
                StartedAt: startedAt,
              });
            });
            progress.BreakRemainingSeconds = null;
            return progress;
          }).then(() => {
            if (!this._mounted) return null;
            this.setState({
              starting: false,
              screen: "case",
              simulation: {
                caseData: caseRow,
                simulationData: result.simulationData,
                caseguid: result.caseguid,
                caseId: entry.CaseId,
                startedAt: startedAt,
              },
            });
            return null;
          });
        }

        return this.api
          .incompleteStart({
            caseguid: result.caseguid,
            currentStateId: match.CurrentStateId,
            simulationData: result.simulationData,
          })
          .then((resumed) => {
            if (!this._mounted) return null;
            return this.saveProgress(function (progress) {
              progress.Cases = progress.Cases.map(function (row) {
                if (row.CaseId !== entry.CaseId) return row;
                return Object.assign({}, row, {
                  caseguid: result.caseguid,
                });
              });
              progress.BreakRemainingSeconds = null;
              return progress;
            }).then(() => {
              if (!this._mounted) return null;
              this.setState({
                starting: false,
                screen: "case",
                simulation: {
                  caseData: caseRow,
                  simulationData: resumed.simulationData,
                  caseguid: result.caseguid,
                  caseId: entry.CaseId,
                  // Elapsed time picks up from the original start, not now.
                  startedAt: entry.StartedAt || new Date().toISOString(),
                },
              });
              return null;
            });
          });
      })
      .catch((error) => {
        if (!this._mounted) return;
        if (error && error.isAuthError) {
          this.setState({ starting: false });
          return;
        }
        this.setState({
          starting: false,
          startError:
            error && error.isCaseListError
              ? String(error.message).replace("Error: ", "")
              : "That case wouldn't resume. Check your connection and try again.",
        });
      });
  }

  // Passed as <Simulation>'s displayGrading prop. Unlike Multi CCS, there is
  // no network call to make here: CDM's own Simulation.fetchFinalQuestionSubmission
  // already POSTed the full answer set to /grading.webapi and already called
  // triggerGamificationEvent itself before invoking this — gradingData is that
  // response, handed to us synchronously. All this does is bank it into the
  // block's CaseProgress and route back to the break screen without ever
  // showing the grade (same as Multi CCS: grades stay hidden until MultiCdmReview).
  //
  // practiceMode is always false for a block (see renderSimulation), so this is
  // always called with real gradingData — never the no-argument path
  // Simulation's continueCaseHandler uses for practice mode.
  handleGradeCase = (gradingData) => {
    const simulation = this.state.simulation;
    if (!simulation) return;
    // Belt-and-suspenders: nothing should call this twice for one case, but
    // don't double-write the progress blob if it happens.
    if (this.state.grading) return;

    this.setState({ grading: true });

    const completedAt = new Date().toISOString();
    const total =
      gradingData && typeof gradingData.TotalGrade === "number"
        ? gradingData.TotalGrade
        : null;

    this.saveProgress(function (progress) {
      progress.Cases = progress.Cases.map(function (row) {
        if (row.CaseId !== simulation.caseId) return row;
        return Object.assign({}, row, {
          Status: CASE_STATUS.COMPLETED,
          caseguid: simulation.caseguid,
          CurrentStateId: null,
          GradeForCase: total,
          CompletedAt: completedAt,
          ElapsedSeconds: elapsedSeconds(simulation.startedAt, completedAt),
        });
      });
      // A fresh break for the next case.
      progress.BreakRemainingSeconds = null;
      return progress;
    }).then((session) => {
      if (!this._mounted) return;

      // Keep COMPLETED / AvgGradeForCase current for the builder and the
      // break screen. Fire and forget.
      this.fetchCases();

      const done = session ? isSessionComplete(session.CaseProgress) : false;
      this.setState({
        grading: false,
        simulation: null,
        screen: done ? "review" : "break",
      });
    });
  };

  // Simulation's "Exit Case" -> confirmExitCase popup -> Yes. The backend has
  // already been recording CurrentStateId as orders went in; the next
  // getMultiCdmCase fills it into the blob for us.
  handleExitCase = () => {
    const session = this.state.session;
    if (!session) {
      this.backToSessions();
      return;
    }

    this.setState({ simulation: null, screen: "break" });

    this.api
      .getMultiCdmCase(session.ID)
      .then((fresh) => {
        if (!this._mounted || !fresh) return;
        this.setState({ session: fresh });
      })
      .catch(function () {
        // The local copy still says in_progress with the right caseguid, so a
        // failure here only costs us the CurrentStateId until the next read.
      });
  };

  // ── Review-later, shared between the grade view and the case list ─────────

  handleReviewLater = (caseId, marked) => {
    this.setState(function (prevState) {
      return {
        cases: prevState.cases.map(function (row) {
          if (row.ID !== caseId) return row;
          return Object.assign({}, row, { ReviewLaterFlag: marked });
        }),
      };
    });
  };

  handleReadingOptions = (next) => {
    this.setState({ readingOptions: next });
    // Go through App's saver when we have it: it's the single merging copy
    // of the blob. The api layer keeps its own stale copy from mount, so
    // saving through it after a skin/text-size change in a case would post
    // the old simulation fields back and undo that change.
    if (this.props.saveReadingOptions) {
      this.props.saveReadingOptions(next);
    } else {
      this.api.saveReadingOptions(next);
    }
  };

  reviewLaterIds() {
    return this.state.cases
      .filter(function (row) {
        return !!row.ReviewLaterFlag;
      })
      .map(function (row) {
        return row.ID;
      });
  }

  // ── Render ───────────────────────────────────────────────────────────────

  renderSimulation() {
    const simulation = this.state.simulation;
    const session = this.state.session;
    const settings = session.CaseProgress.Settings;

    // Simulation reads timedExam / timerSpeed exactly the way the main app
    // feeds them, so the block's settings map straight onto its own timer.
    // (The speed is actually applied earlier, to timeRemainingInSeconds, by
    // api.startCase/incompleteStart — timerSpeed here only drives the header's
    // display, same as App.js.)
    const timedExam = !!settings.EnforceCaseTimeLimit;

    return (
      <div className="mcdm-sim">
        <Simulation
          // Same settings plumbing as App.js's own <Simulation>. Without
          // these a block fell back to the default skin, hid the skin
          // switcher, and its text size / split screen / confirm-before-Next
          // settings neither loaded from nor saved to the user's blob.
          simulationSkin={this.props.simulationSkin}
          simulationSkins={this.props.simulationSkins}
          changeSimulationSkin={this.props.changeSimulationSkin}
          savedReadingOptions={this.props.savedReadingOptions}
          saveReadingOptions={this.props.saveReadingOptions}
          key={simulation.caseguid}
          caseData={simulation.caseData}
          selectedCaseId={simulation.caseId}
          caseguid={simulation.caseguid}
          simulationData={simulation.simulationData}
          userData={this.props.userData}
          userProfile={this.props.userProfile}
          route={this.props.route}
          timedExam={timedExam}
          timerSpeed={settings.TimerSpeed}
          // A block always runs full exam conditions — practice mode's
          // "grade one question at a time, then Continue" flow doesn't fit a
          // block any better than it fit Multi CCS's exam-day framing.
          practiceMode={false}
          fetchOutstanding={this.state.fetchOutstanding}
          setFetchOutstanding={(value) => {
            this.setState({ fetchOutstanding: value });
          }}
          // CDM's own Simulation POSTs to /grading.webapi and calls this
          // directly on the final question — unlike Multi CCS there's no
          // double-fire risk to guard against, so this is the real thing, not
          // a stub.
          triggerGamificationEvent={this.api.triggerGamificationEvent}
          displayGrading={this.handleGradeCase}
          exitSimulation={this.handleExitCase}
          setAuthenticationError={this.props.setAuthenticationError}
        />
      </div>
    );
  }

  render() {
    const {
      screen,
      session,
      cases,
      casesLoading,
      casesError,
      sessions,
      sessionsLoading,
      sessionsError,
      simulation,
    } = this.state;
    let body;

    if (screen === "case" && simulation && session) {
      body = this.renderSimulation();
    } else if (screen === "builder") {
      body = (
        <MultiCdmCaseBuilder
          cases={cases}
          loading={casesLoading}
          error={casesError}
          creating={this.state.creating}
          createError={this.state.createError}
          initialSettings={this.state.rememberedSettings}
          onCreate={this.handleCreate}
          onCancel={this.backToSessions}
          onRetry={this.fetchCases}
        />
      );
    } else if (screen === "break" && session) {
      // The case list is needed for titles and time limits.
      if (casesLoading) {
        body = (
          <div className="mcdm-loading">
            <MDSpinner size={50} singleColor={this.props.primaryColor} />
            <p>Loading cases...</p>
          </div>
        );
      } else {
        body = (
          <MultiCdmBreakScreen
            session={session}
            cases={cases}
            nextEntry={nextCaseEntry(session.CaseProgress)}
            starting={this.state.starting || this.state.grading}
            startError={this.state.startError}
            savingSettings={this.state.savingSettings}
            onContinue={this.handleContinue}
            onTick={this.handleBreakTick}
            onSettings={this.handleSettings}
            onExit={this.backToSessions}
          />
        );
      }
    } else if (screen === "review" && session) {
      body = (
        <MultiCdmReview
          session={session}
          cases={cases}
          api={this.api}
          userData={this.props.userData}
          userProfile={this.props.userProfile}
          reviewLaterIds={this.reviewLaterIds()}
          onReviewLater={this.handleReviewLater}
          readingOptions={
            this.props.savedReadingOptions || this.state.readingOptions
          }
          onReadingOptions={this.handleReadingOptions}
          onExit={this.backToSessions}
          primaryColor={this.props.primaryColor}
          selectedTheme={this.props.selectedTheme}
          themeNumber={this.props.themeNumber}
        />
      );
    } else {
      body = (
        <MultiCdmSessionList
          sessions={sessions}
          loading={sessionsLoading}
          error={sessionsError}
          renamingId={this.state.renamingId}
          onNewBlock={this.handleNewBlock}
          onOpenSession={this.handleOpenSession}
          onRenameSession={this.handleRenameSession}
          onRetry={this.fetchSessions}
          onExit={this.props.onExit}
        />
      );
    }

    return <div className="mcdm-root">{body}</div>;
  }
}

export default MultiCdmApp;
